import React, { useState, useEffect } from "react";
import { loader, snackbar } from "../../utils";
import { Modal, ModalBody } from "reactstrap";
import { baseUrl } from "../../Components/service/Api";
import {
  createAdsBanner,
  getAllAdsBanners,
} from "../../Components/service/admin";

const bannerTypes = [
  { key: "defaultBanner", label: "Default Banner", size: "1920 X 1080" },
  { key: "banner_one", label: "Banner One", size: "900 X 230" },
  { key: "banner_two", label: "Banner Two", size: "900 X 230" },
  { key: "popup_banner", label: "Banner Popup", size: "900 X 230" },
  { key: "banner_three", label: "Banner Three", size: "900 X 230" },
  { key: "banner_four", label: "Banner Four", size: "900 X 230" },
];

const AdsBanner = () => {
  const [banners, setBanners] = useState({});
  const [previews, setPreviews] = useState({});
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("tv");
  const [activeTab, setActiveTab] = useState("tv");

  const filteredBannerTypes =
    type === "mobile" ? bannerTypes.slice(0, 4) : bannerTypes;

  // ---------------- Fetch Banners ----------------
  const fetchBanners = async (tab) => {
    loader.start();
    try {
      const res = await getAllAdsBanners(tab);
      if (res.data?.data) {
        setBanners(res.data.data);

        const imgPrev = {};
        // Only process banners for current tab
        const currentBannerTypes =
          tab === "mobile" ? bannerTypes.slice(0, 4) : bannerTypes;
        currentBannerTypes.forEach((b) => {
          if (res.data.data[b.key]) {
            imgPrev[b.key] = `${baseUrl}/${res.data.data[b.key]}`;
          }
        });
        setPreviews(imgPrev);
      } else {
        // Clear banners and previews when no data is returned
        setBanners({});
        setPreviews({});
      }
    } finally {
      loader.stop();
    }
  };

  useEffect(() => {
    fetchBanners(activeTab);
  }, [activeTab]);

  // ------------- Handle File Change -------------
  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelected({ ...selected, [key]: file });
    setPreviews({ ...previews, [key]: URL.createObjectURL(file) });
  };

  // ---------------- Save Banners ----------------
  const saveBanners = async () => {
    // Validate that at least one banner is selected
    const hasSelectedBanners = Object.keys(selected).length > 0;
    if (!hasSelectedBanners) {
      snackbar.error("Please select at least one banner to upload");
      return;
    }

    loader.start();
    const formData = new FormData();

    bannerTypes.forEach((b) => {
      if (selected[b.key]) {
        formData.append(b.key, selected[b.key]);
      }
    });

    // Add type to form data
    formData.append("type", type);

    try {
      const res = await createAdsBanner(formData);
      if (!res.data?.error) {
        snackbar.success("Banners updated successfully");
        setOpen(false);
        setSelected({});
        fetchBanners(activeTab);
      } else {
        // Show specific error message from backend
        const errorMsg = res.data?.errormessage || "Error updating banners";
        snackbar.error(errorMsg);
      }
    } catch (err) {
      // Handle network errors or other exceptions
      const errorMsg =
        err.response?.data?.errormessage ||
        err.message ||
        "Error updating banners";
      snackbar.error(errorMsg);
    } finally {
      loader.stop();
    }
  };

  const handleTabClick = (tab) => {
    // Reset form data when switching tabs
    setSelected({});
    setPreviews({}); // Clear existing previews
    setType(tab);
    setActiveTab(tab);

    // Fetch banners for the new tab
    fetchBanners(tab);
  };

  return (
    <>
      {/* ---------------- Modal ---------------- */}
      <Modal centered isOpen={open}>
        <ModalBody
          className="p-4"
          style={{
            backgroundColor: "#f9fbfd",
            maxHeight: "500px",
            overflowY: "auto",
          }}
        >
          <h5 className="mb-4 fw-semibold text-dark">Manage Banners</h5>

          <div className="d-flex flex-column gap-3">
            {filteredBannerTypes.map((b) => (
              <div
                key={b.key}
                className="p-3 rounded shadow-sm d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  {/* Label Section */}
                  <div className="d-flex flex-column">
                    <span
                      className="fw-semibold text-dark mb-1"
                      style={{ fontSize: "0.95rem" }}
                    >
                      {b.label}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="badge bg-light text-dark border"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {b.size}
                      </span>
                    </div>
                  </div>

                  {/* Preview Section */}
                  {previews[b.key] && (
                    <div className="ms-auto">
                      <img
                        src={previews[b.key]}
                        alt=""
                        className="rounded shadow-sm"
                        style={{
                          width: 100,
                          height: 56,
                          objectFit: "cover",
                          border: "2px solid #e9ecef",
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Custom File Input */}
                <label
                  htmlFor={`file-${b.key}`}
                  className="btn btn-sm btn-outline-primary px-3 m-1"
                  style={{ fontSize: "0.85rem" }}
                >
                  {previews[b.key] ? "Change" : "Select"}
                </label>

                <input
                  hidden
                  id={`file-${b.key}`}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, b.key)}
                />
              </div>
            ))}
          </div>
          <div className="mb-3">
            <label
              htmlFor="type"
              className="form-label fw-semibold text-secondary mb-1"
            >
              Type
            </label>

            <select
              name="type"
              id="type"
              className="form-select shadow-sm"
              style={{
                borderRadius: "8px",
                padding: "8px 12px",
                borderColor: "#d0d7de",
                backgroundColor: "#f8fafc",
              }}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="tv">TV</option>
              <option value="mobile">Mobile</option>
            </select>
          </div>

          {/* Footer Buttons */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              className="btn btn-light border px-3"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary px-3" onClick={saveBanners}>
              Save
            </button>
          </div>
        </ModalBody>
      </Modal>

      {/* ---------------- Header & Button ---------------- */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3> Advertisement Banners</h3>
        <button className="btn btn-primary" onClick={() => setOpen(true)}>
          Manage Banners
        </button>
      </div>

      {/* ---------------- Tabs To Switch ---------------- */}
      <div className="d-flex gap-2 m-2">
        <div
          className={`px-4 py-2 rounded cursor-pointer 
      ${
        activeTab === "tv"
          ? "bg-primary text-white shadow-sm"
          : "bg-light border"
      }
    `}
          onClick={() => handleTabClick("tv")}
        >
          TV
        </div>

        <div
          className={`px-4 py-2 rounded cursor-pointer 
      ${
        activeTab === "mobile"
          ? "bg-primary text-white shadow-sm"
          : "bg-light border"
      }
    `}
          onClick={() => handleTabClick("mobile")}
        >
          Mobile
        </div>
      </div>

      {/* ---------------- Simple Listing ---------------- */}
      <div className="bg-white rounded border" key={activeTab}>
        {filteredBannerTypes.map((b, index) => (
          <div
            key={`${activeTab}-${b.key}`}
            className={`p-3 d-flex align-items-center ${
              index !== filteredBannerTypes.length - 1 ? "border-bottom" : ""
            }`}
          >
            <div className="flex-grow-1">
              <h6 className="mb-0 fw-500">{b.label}</h6>
            </div>

            <div className="text-end">
              {banners && banners[b.key] ? (
                <img
                  src={`${baseUrl}/${banners[b.key]}`}
                  alt={b.label}
                  style={{
                    width: "100px",
                    height: "60px",
                    objectFit: "cover",
                    display: "block",
                  }}
                  className="rounded border"
                />
              ) : (
                <span className="text-muted small">No banner</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AdsBanner;
